export type Point = {
  x: number;
  y: number;
};

export type Ray = {
  origin: Point;
  direction: Point;
};

export type Box = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};

export type RayIntersection = {
  type: "horizontal" | "vertical";
  value: number;
};

export const vectorLength = (point: Point) =>
  Math.sqrt(point.x * point.x + point.y * point.y);

export const identityVector = (point: Point) => {
  const l = vectorLength(point);
  return { x: point.x / l, y: point.y / l };
};

export const vectorMul = (coefficient: number, point: Point) => {
  return { x: coefficient * point.x, y: coefficient * point.y };
};

export const vectorAdd = (a: Point, b: Point): Point => {
  return {
    x: a.x + b.x,
    y: a.y + b.y,
  };
};

export const vectorSub = (a: Point, b: Point): Point => {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
  };
};

export const distance = (a: Point, b: Point): number => {
  return vectorLength(vectorSub(a, b));
};

export const rayBoxIntersection = (
  box: Box,
  ray: Ray
): RayIntersection | null => {
  const xTop =
    ray.direction.y >= 0 || box.top > ray.origin.y
      ? null
      : ray.origin.x +
        (box.top - ray.origin.y) * (ray.direction.x / ray.direction.y);
  const xBottom =
    ray.direction.y <= 0 || box.bottom < ray.origin.y
      ? null
      : ray.origin.x +
        (box.bottom - ray.origin.y) * (ray.direction.x / ray.direction.y);
  const yLeft =
    ray.direction.x <= 0 || box.left < ray.origin.x
      ? null
      : ray.origin.y +
        (box.left - ray.origin.x) * (ray.direction.y / ray.direction.x);
  const yRight =
    ray.direction.x >= 0 || box.right > ray.origin.x
      ? null
      : ray.origin.y +
        (box.right - ray.origin.x) * (ray.direction.y / ray.direction.x);
  const intersectionTop =
    xTop !== null && xTop >= box.left && xTop < box.right
      ? (box.top - ray.origin.y) / ray.direction.y
      : Infinity;
  const intersectionBottom =
    xBottom !== null && xBottom >= box.left && xBottom < box.right
      ? (box.bottom - ray.origin.y) / ray.direction.y
      : Infinity;
  const intersectionLeft =
    yLeft !== null && yLeft >= box.bottom && yLeft < box.top
      ? (box.left - ray.origin.x) / ray.direction.x
      : Infinity;
  const intersectionRight =
    yRight !== null && yRight >= box.bottom && yRight < box.top
      ? (box.right - ray.origin.x) / ray.direction.x
      : Infinity;
  const horizontal = Math.min(intersectionTop, intersectionBottom);
  const vertical = Math.min(intersectionLeft, intersectionRight);
  if (horizontal === Infinity && vertical === Infinity) {
    return null;
  } else if (horizontal < vertical) {
    return { value: horizontal, type: "horizontal" };
  } else {
    return { value: vertical, type: "vertical" };
  }
};
